import {
  addBeforeItem,
  addDocument,
  bulkActivities,
  countryName,
  DatePick,
  documentFileUrl,
  filesZipAddress,
  formatMoney,
  getDocs,
  getLists,
  getNotices,
  getPlan,
  NotFound,
  Pick,
  removeDocument,
  setNotices,
  setReminder,
  setZone,
  telegramLink,
  testMail,
  tickTask,
  toIso,
  untieTelegram,
  updateDocument,
  uploadDocumentFile,
  type BulkChange,
  type NoticeEvent,
  type NoticePref,
  type Notices,
  type Plan,
  type Task,
  type TripDocument,
} from '@trippath/shared';
import { useEffect, useRef, useState, type DragEvent, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import type { TFunction } from 'i18next';

import { useLive } from '../../app/live';
import { ReadOnlySet, useReadOnly } from '../../app/readOnly';
import { dayRange } from '../../lib/countryFacts';

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; plan: Plan; tasks: Task[]; documents: TripDocument[] };

/** A failure said nowhere: the page stays as it was, and the next load says what is. */
const ignore = () => undefined;

/** The kinds told of on this page, in its order, and how each is told. */
const EVENTS: NoticeEvent[] = ['before', 'invite', 'companion', 'debt', 'weekly'];
const CHANNELS = ['email', 'push', 'telegram'] as const;

/** A day as `10 Feb`, a time as `09:00`, local here. */
function dayOf(iso: string, language: string): string {
  return new Intl.DateTimeFormat(language === 'et' ? 'et-EE' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(`${iso.slice(0, 10)}T00:00:00Z`));
}

/** When a reminder rings, local on this device: `10 Feb 09:00`, or the day alone. */
function ringsAt(at: string, timed: boolean, language: string): string {
  const when = new Date(at);
  return new Intl.DateTimeFormat(language === 'et' ? 'et-EE' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    ...(timed ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(when);
}

/** A day and a clock time on this device as an instant: `2027-02-10T07:00:00Z`. */
function instantOf(day: string, clock: string): string {
  return new Date(`${day}T${clock}`).toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/** The half hours of a day a reminder may ring at. */
const CLOCKS = Array.from({ length: 34 }, (_, at) => {
  const minutes = 6 * 60 + at * 30;
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
});

/** A reminder's time, or none: the day alone. */
function ClockPick({ value, onChange }: { value: string; onChange: (clock: string) => void }) {
  const { t } = useTranslation();
  return (
    <Pick
      label={t('lists.remindTime')}
      searchable={false}
      options={[
        { value: '', label: t('lists.anyTime') },
        ...CLOCKS.map((clock) => ({ value: clock, label: clock })),
      ]}
      value={value}
      onChange={onChange}
    />
  );
}

/** What a task says: what it is and what it is about. */
function taskWords(
  task: Task,
  t: TFunction,
  language: string,
): { what: string; about: string } {
  const country = task.country ? countryName(task.country, language) : '';
  switch (task.kind) {
    case 'border':
      return {
        what: t('lists.task.border', { form: task.title, country }),
        about: t('lists.task.borderAbout', {
          country,
          days: Math.round(Number(task.hours) / 24),
        }),
      };
    case 'pay':
      return {
        what: t('lists.task.pay', { name: task.title }),
        about: [
          t('lists.task.payAbout', { city: String(task.city) }),
          task.amount ? formatMoney(task.amount, language) : null,
          task.until ? t('lists.task.free', { day: dayOf(task.until, language) }) : null,
        ]
          .filter(Boolean)
          .join(' · '),
      };
    case 'checkin':
      return {
        what: t('lists.task.checkin', { flight: task.title }),
        about: t('lists.task.checkinAbout', { hours: task.hours }),
      };
    case 'maps':
      return { what: t('lists.task.maps'), about: t('lists.task.mapsAbout') };
    default:
      return { what: task.title, about: task.remindAt ? '' : t('lists.task.noDay') };
  }
}

/** A task's row: ticked or not, what it is, and when it rings. */
function TaskRow({
  task,
  trip,
  onTick,
  onRemind,
}: {
  task: Task;
  trip: string;
  /** Told it is ticked or not: what it gives back done, the tick kept; failed, taken back. */
  onTick: (done: boolean) => Promise<unknown>;
  onRemind: (remindAt: string | null, timed: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [editing, setEditing] = useState(false);
  const [day, setDay] = useState(task.remindAt?.slice(0, 10) ?? '');
  const [clock, setClock] = useState('');
  // Ticked at once, before the server says so: kept while the task still says what it said
  // when pressed; read again, the task's own word.
  const [ticked, setTicked] = useState<{ done: boolean; was: boolean } | null>(null);
  const done = ticked?.was === task.done ? ticked.done : task.done;
  const { what, about } = taskWords(task, t, lang);
  const rings = task.remindAt && ringsAt(task.remindAt, task.timed, lang);
  return (
    <li className={done ? 'lists-task lists-done' : 'lists-task'}>
      <input
        type="checkbox"
        checked={done}
        aria-label={what}
        onChange={(event) => {
          const now = event.target.checked;
          setTicked({ done: now, was: task.done });
          onTick(now).catch(() => {
            setTicked(null);
          });
        }}
      />
      <span>
        <span className="lists-what">{what}</span>
        <small>
          {task.done && task.doneAt
            ? t('lists.doneOn', { day: dayOf(task.doneAt, lang) })
            : about}
          {task.kind === 'maps' && !task.done && (
            <>
              {' · '}
              <Link to={`/trips/${trip}/offline`}>{t('lists.task.mapsOpen')}</Link>
            </>
          )}
        </small>
        {editing && (
          <span className="lists-remind-form">
            <DatePick
              label={t('lists.remindDay')}
              value={day || null}
              onChange={(picked) => {
                setDay(picked ?? '');
              }}
            />
            <ClockPick value={clock} onChange={setClock} />
            <button
              type="button"
              className="button button-small"
              disabled={!day}
              onClick={() => {
                setEditing(false);
                onRemind(instantOf(day, clock || '09:00'), Boolean(clock));
              }}
            >
              {t('lists.save')}
            </button>
            {task.remindAt && (
              <button
                type="button"
                className="button button-small"
                onClick={() => {
                  setEditing(false);
                  onRemind(null, true);
                }}
              >
                {t('lists.noReminder')}
              </button>
            )}
          </span>
        )}
      </span>
      {task.kind === 'own' && !task.done ? (
        <button
          type="button"
          className="lists-rings"
          aria-expanded={editing}
          onClick={() => {
            setEditing(!editing);
          }}
        >
          🔔 {rings || t('lists.remind')}
        </button>
      ) : (
        <span className="lists-rings">{rings && !task.done ? `🔔 ${rings}` : ''}</span>
      )}
    </li>
  );
}

/** What to do before the trip: overdue, this week, before leaving, with no day, done. */
function Tasks({
  plan,
  tasks,
  onChanged,
}: {
  plan: Plan;
  tasks: Task[];
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const trip = plan.trip.id;
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState('');
  const [day, setDay] = useState('');
  const [clock, setClock] = useState('');
  const [today] = useState(() => toIso(new Date()));
  const weekEnd = toIso(new Date(new Date(`${today}T12:00:00`).getTime() + 6 * 86_400_000));
  const leaving = toIso(
    new Date(new Date(`${plan.trip.start}T12:00:00`).getTime() - 86_400_000),
  );
  const open = tasks.filter((task) => !task.done);
  const groups: { key: string; label: string; late?: boolean; tasks: Task[] }[] = [
    {
      key: 'late',
      label: t('lists.late'),
      late: true,
      tasks: open.filter((task) => task.due !== null && task.due < today),
    },
    {
      key: 'week',
      label: t('lists.week', { range: dayRange(today, weekEnd, lang) }),
      tasks: open.filter(
        (task) => task.due !== null && task.due >= today && task.due <= weekEnd,
      ),
    },
    {
      key: 'later',
      label: t('lists.later', { day: dayOf(leaving, lang) }),
      tasks: open.filter((task) => task.due !== null && task.due > weekEnd),
    },
    { key: 'none', label: t('lists.none'), tasks: open.filter((task) => task.due === null) },
    { key: 'done', label: t('lists.done'), tasks: tasks.filter((task) => task.done) },
  ];
  const add = (event: SyntheticEvent) => {
    event.preventDefault();
    addBeforeItem(trip, text)
      .then((item) =>
        day
          ? setReminder(trip, item.id, instantOf(day, clock || '09:00'), Boolean(clock))
          : null,
      )
      .then(() => {
        setText('');
        setDay('');
        setClock('');
        setAdding(false);
        onChanged();
      }, ignore);
  };
  return (
    <section className="panel" aria-labelledby="lists-tasks">
      <div className="panel-head">
        <h2 id="lists-tasks" className="panel-title">
          {t('lists.tasks')}
        </h2>
        <span className="small quiet">
          {t('lists.count', { done: tasks.length - open.length, all: tasks.length })}
        </span>
        <button
          type="button"
          className="button button-small"
          aria-expanded={adding}
          onClick={() => {
            setAdding(!adding);
          }}
        >
          {t('lists.add')}
        </button>
      </div>
      {adding && (
        <form className="lists-add" onSubmit={add}>
          <input
            aria-label={t('lists.what')}
            placeholder={t('lists.what')}
            maxLength={200}
            required
            value={text}
            onChange={(event) => {
              setText(event.target.value);
            }}
          />
          <DatePick
            label={t('lists.remindDay')}
            value={day || null}
            onChange={(picked) => {
              setDay(picked ?? '');
            }}
          />
          <ClockPick value={clock} onChange={setClock} />
          <button type="submit" className="button button-small button-primary">
            {t('lists.save')}
          </button>
        </form>
      )}
      {groups
        .filter((group) => group.tasks.length > 0)
        .map((group) => (
          <div key={group.key} className="lists-group">
            <h3 className={group.late ? 'lists-when lists-late' : 'lists-when'}>
              <span>{group.label}</span>
              <span>{group.tasks.length}</span>
            </h3>
            <ul className="lists-tasks">
              {group.tasks.map((task) => (
                <TaskRow
                  key={task.key}
                  task={task}
                  trip={trip}
                  onTick={(done) => tickTask(trip, task.key, done).then(onChanged)}
                  onRemind={(remindAt, timed) => {
                    void setReminder(trip, task.key.slice(4), remindAt, timed).then(
                      onChanged,
                      ignore,
                    );
                  }}
                />
              ))}
            </ul>
          </div>
        ))}
      <p className="small quiet">{t('lists.tasksNote')}</p>
    </section>
  );
}

/** Kilobytes or megabytes, as said here: `14,6 MB`. */
function sizeOf(bytes: number, language: string): string {
  const megabytes = bytes / 1_000_000;
  return megabytes >= 0.1
    ? `${megabytes.toLocaleString(language, { maximumFractionDigits: 1 })} MB`
    : `${Math.max(bytes > 0 ? 1 : 0, Math.round(bytes / 1000)).toLocaleString(language)} kB`;
}

/** The trip's files: each with what it belongs to, dropped in, all of them as a ZIP. */
function Files({
  plan,
  documents,
  onChanged,
}: {
  plan: Plan;
  documents: TripDocument[];
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locked = useReadOnly();
  const lang = i18n.language;
  const trip = plan.trip.id;
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const files = documents.filter(
    (document): document is TripDocument & { file: { type: string; size: number } } =>
      document.file !== null,
  );
  const total = files.reduce((sum, document) => sum + document.file.size, 0);
  const bookings = plan.stops.flatMap((stop) =>
    stop.bookings.map((booking) => ({ booking, stop })),
  );
  const tiedTo = (document: TripDocument) => {
    const booked = bookings.find(({ booking }) => booking.id === document.booking);
    const stop = plan.stops.find((one) => one.id === document.stop);
    const thing = plan.stops
      .flatMap((one) => one.agenda)
      .find((one) => one.id === document.activity);
    if (booked) return t('lists.tied', { what: `${booked.booking.name}, ${booked.stop.city}` });
    if (thing) return t('lists.tied', { what: thing.name });
    if (stop) {
      return t('lists.tied', { what: `${stop.city}, ${dayRange(stop.start, stop.end, lang)}` });
    }
    return t('lists.wholeTrip');
  };
  const upload = (chosen: File[]) => {
    setBusy(true);
    const each = chosen.map((file) =>
      addDocument(trip, {
        kind: 'other',
        name: file.name.slice(0, 200),
        note: '',
        validFrom: null,
        validTo: null,
        stop: null,
        private: false,
        traveller: null,
      }).then((document) => uploadDocumentFile(trip, document.id, file)),
    );
    void Promise.allSettled(each).then(() => {
      setBusy(false);
      onChanged();
    });
  };
  const tie = (document: TripDocument, to: string) => {
    const [kind, id = null] = to.split(':');
    void updateDocument(trip, document.id, {
      kind: document.kind,
      name: document.name,
      note: document.note,
      validFrom: document.validFrom,
      validTo: document.validTo,
      stop: kind === 'stop' ? id : null,
      private: document.private,
      traveller: document.traveller,
      activity: document.activity,
      booking: kind === 'booking' ? id : null,
      // Its price kept, in the money it was paid in.
      price: (document.paid ?? document.price)?.minor ?? null,
      currency: document.paid?.currency ?? null,
    }).then(onChanged, ignore);
  };
  const options = [
    { value: 'trip', label: t('lists.wholeTrip') },
    ...plan.stops.map((stop) => ({ value: `stop:${stop.id}`, label: stop.city })),
    ...bookings.map(({ booking, stop }) => ({
      value: `booking:${booking.id}`,
      label: `${booking.name}, ${stop.city}`,
    })),
  ];
  return (
    <section className="panel" aria-labelledby="lists-files">
      <div className="panel-head">
        <h2 id="lists-files" className="panel-title">
          {t('lists.files')}
        </h2>
        <span className="small quiet">
          {t('lists.filesCount', { count: files.length, size: sizeOf(total, lang) })}
        </span>
        {files.length > 0 && (
          <a className="button button-small" href={filesZipAddress(trip, plan.trip.name)}>
            {t('lists.zip')}
          </a>
        )}
      </div>
      <button
        type="button"
        className={over ? 'lists-drop lists-over' : 'lists-drop'}
        disabled={busy}
        onClick={() => picker.current?.click()}
        onDragOver={(event: DragEvent) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => {
          setOver(false);
        }}
        onDrop={(event: DragEvent) => {
          event.preventDefault();
          setOver(false);
          // A drop reaches a button held still, too: nothing taken into a trip read only.
          if (!locked) upload([...event.dataTransfer.files]);
        }}
      >
        <b>{busy ? t('lists.uploading') : t('lists.drop')}</b>
        <span className="small quiet">{t('lists.dropAbout')}</span>
      </button>
      <input
        ref={picker}
        type="file"
        multiple
        hidden
        accept="application/pdf,image/*"
        aria-label={t('lists.drop')}
        onChange={(event) => {
          upload([...(event.target.files as FileList)]);
          event.target.value = '';
        }}
      />
      <ul className="lists-files">
        {files.map((document) => {
          const pdf = document.file.type === 'application/pdf';
          const tied = document.booking
            ? `booking:${document.booking}`
            : document.stop
              ? `stop:${document.stop}`
              : 'trip';
          return (
            <li key={document.id}>
              <span className={pdf ? 'lists-ext' : 'lists-ext lists-image'}>
                {pdf ? 'PDF' : 'IMG'}
              </span>
              <span>
                <a href={documentFileUrl(trip, document.id)} target="_blank" rel="noreferrer">
                  <b>{document.name}</b>
                </a>
                <small>{tiedTo(document)}</small>
              </span>
              <span className="lists-file-actions">
                <Pick
                  label={t('lists.tieTo', { name: document.name })}
                  quiet
                  searchable={false}
                  options={options}
                  value={tied}
                  onChange={(to) => {
                    tie(document, to);
                  }}
                />
                {removing === document.id ? (
                  <button
                    type="button"
                    className="button button-small button-danger"
                    onClick={() => {
                      setRemoving(null);
                      void removeDocument(trip, document.id).then(onChanged, ignore);
                    }}
                  >
                    {t('lists.removeSure')}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="button button-small"
                    aria-label={t('lists.remove', { name: document.name })}
                    onClick={() => {
                      setRemoving(document.id);
                    }}
                  >
                    ×
                  </button>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="small quiet">{t('lists.filesNote')}</p>
    </section>
  );
}

/** A stop's things done, several changed at once. */
function Things({ plan, onPlan }: { plan: Plan; onPlan: (plan: Plan) => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const stops = plan.stops.filter((stop) => stop.agenda.length > 0);
  const [stopId, setStopId] = useState(stops[0]?.id ?? '');
  const [chosen, setChosen] = useState<string[]>([]);
  const [doing, setDoing] = useState<'day' | 'copy' | 'tag' | 'delete' | null>(null);
  const [tag, setTag] = useState('');
  const stop = stops.find((one) => one.id === stopId) ?? stops[0];
  if (!stop) {
    return (
      <section className="panel" aria-labelledby="lists-things">
        <h2 id="lists-things" className="panel-title">
          {t('lists.thingsNone')}
        </h2>
        <p className="quiet">{t('lists.thingsNoneAbout')}</p>
      </section>
    );
  }
  const days: string[] = [];
  for (
    let at = new Date(`${stop.start}T12:00:00Z`);
    toIso(at) < stop.end || days.length === 0;
    at = new Date(at.getTime() + 86_400_000)
  ) {
    days.push(at.toISOString().slice(0, 10));
  }
  const dayLabel = (day: string) =>
    new Intl.DateTimeFormat(lang === 'et' ? 'et-EE' : 'en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    }).format(new Date(`${day}T00:00:00Z`));
  const apply = (change: BulkChange) => {
    setDoing(null);
    void bulkActivities(plan.trip.id, chosen, change).then((next) => {
      setChosen(change.change === 'delete' || change.change === 'copy' ? [] : chosen);
      onPlan(next);
    }, ignore);
  };
  const flip = (id: string, on: boolean) => {
    setChosen(on ? [...chosen, id] : chosen.filter((one) => one !== id));
  };
  const said = t('lists.chosen', { count: chosen.length });
  return (
    <section className="panel" aria-labelledby="lists-things">
      <div className="panel-head">
        <h2 id="lists-things" className="panel-title">
          {t('lists.things', { city: stop.city })}
        </h2>
        {stops.length > 1 && (
          <Pick
            label={t('lists.stop')}
            quiet
            searchable={false}
            options={stops.map((one) => ({ value: one.id, label: one.city }))}
            value={stop.id}
            onChange={(id) => {
              setStopId(id);
              setChosen([]);
            }}
          />
        )}
        <span className="small quiet">{t('lists.thingsHow')}</span>
      </div>
      {chosen.length > 0 && (
        <div className="lists-bulk" role="toolbar" aria-label={said}>
          <b>{said}</b>
          <span className="lists-grow" />
          {doing === 'day' || doing === 'copy' ? (
            <Pick
              label={t(doing === 'day' ? 'lists.moveTo' : 'lists.copyTo')}
              searchable={false}
              options={days.map((day) => ({ value: day, label: dayLabel(day) }))}
              value=""
              onChange={(day) => {
                apply({ change: doing, day });
              }}
            />
          ) : null}
          {doing === 'tag' && (
            <form
              className="lists-tag"
              onSubmit={(event) => {
                event.preventDefault();
                apply({ change: 'tag', tag });
                setTag('');
              }}
            >
              <input
                aria-label={t('lists.tagName')}
                maxLength={30}
                required
                value={tag}
                onChange={(event) => {
                  setTag(event.target.value);
                }}
              />
              <button type="submit" className="button button-small">
                {t('lists.save')}
              </button>
            </form>
          )}
          <button
            type="button"
            className="button button-small"
            onClick={() => {
              setDoing('day');
            }}
          >
            {t('lists.move')}
          </button>
          <button
            type="button"
            className="button button-small"
            onClick={() => {
              const booked = stop.agenda.some((one) => chosen.includes(one.id) && !one.booked);
              apply({ change: 'booked', booked });
            }}
          >
            {t('lists.status')}
          </button>
          <button
            type="button"
            className="button button-small"
            onClick={() => {
              setDoing('tag');
            }}
          >
            {t('lists.tag')}
          </button>
          <button
            type="button"
            className="button button-small"
            onClick={() => {
              setDoing('copy');
            }}
          >
            {t('lists.copy')}
          </button>
          {doing === 'delete' ? (
            <button
              type="button"
              className="button button-small button-danger"
              onClick={() => {
                apply({ change: 'delete' });
              }}
            >
              {t('lists.deleteSure', { count: chosen.length })}
            </button>
          ) : (
            <button
              type="button"
              className="button button-small button-danger"
              onClick={() => {
                setDoing('delete');
              }}
            >
              {t('lists.delete')}
            </button>
          )}
        </div>
      )}
      <div className="table-scroll">
        <table className="data">
          <thead>
            <tr>
              <th>
                <span className="visually-hidden">{t('lists.choose')}</span>
              </th>
              <th>{t('lists.day')}</th>
              <th>{t('lists.time')}</th>
              <th>{t('lists.thing')}</th>
              <th>{t('lists.state')}</th>
              <th className="lists-n">{t('lists.price')}</th>
            </tr>
          </thead>
          <tbody>
            {stop.agenda.map((thing) => {
              const on = chosen.includes(thing.id);
              return (
                <tr key={thing.id} className={on ? 'lists-on' : undefined}>
                  <td>
                    <input
                      type="checkbox"
                      checked={on}
                      aria-label={thing.name}
                      onChange={(event) => {
                        flip(thing.id, event.target.checked);
                      }}
                    />
                  </td>
                  <td>{dayLabel(thing.day)}</td>
                  <td className="mono">{thing.starts ?? '—'}</td>
                  <td>
                    {thing.name}
                    {thing.tags.map((one) => (
                      <span key={one} className="tag tag-plain lists-chip">
                        {one}
                      </span>
                    ))}
                  </td>
                  <td>
                    <span className={thing.booked ? 'tag tag-ok' : 'tag tag-plain'}>
                      {t(thing.booked ? 'lists.booked' : 'lists.planned')}
                    </span>
                  </td>
                  <td className="mono lists-n">
                    {thing.price ? formatMoney(thing.price, lang) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** How one is told: each kind by mail, in the browser, by Telegram; a test mail; Telegram tied. */
function Telling({
  notices,
  onNotices,
}: {
  notices: Notices;
  onNotices: (n: Notices) => void;
}) {
  const { t } = useTranslation();
  const [said, setSaid] = useState<string | null>(null);
  const flip = (event: NoticeEvent, how: (typeof CHANNELS)[number]) => {
    const prefs: NoticePref[] = notices.prefs.map((pref) =>
      pref.event === event ? { ...pref, [how]: !pref[how] } : pref,
    );
    onNotices({ ...notices, prefs });
    void setNotices(prefs).catch(ignore);
  };
  const can = (event: NoticeEvent, how: (typeof CHANNELS)[number]) => {
    if (how === 'email') return notices.mail && event !== 'invite';
    if (how === 'telegram') return notices.telegram.tied;
    return true;
  };
  const why = (event: NoticeEvent, how: (typeof CHANNELS)[number]) => {
    if (how === 'email') return t(event === 'invite' ? 'lists.inviteMail' : 'lists.noMail');
    return t(notices.telegram.on ? 'lists.notTied' : 'lists.noBot');
  };
  return (
    <section className="panel" aria-labelledby="lists-notices">
      <div className="panel-head">
        <h2 id="lists-notices" className="panel-title">
          {t('lists.notices')}
        </h2>
        <span className="small quiet">{notices.email}</span>
      </div>
      <div className="table-scroll">
        <table className="data lists-notices">
          <thead>
            <tr>
              <th>{t('lists.whatTold')}</th>
              {CHANNELS.map((how) => (
                <th key={how}>{t(`lists.channel.${how}`)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {EVENTS.map((event) => {
              const pref = notices.prefs.find((one) => one.event === event);
              return (
                pref && (
                  <tr key={event}>
                    <td>
                      {t(`lists.event.${event}.name`)}
                      <small>{t(`lists.event.${event}.about`)}</small>
                    </td>
                    {CHANNELS.map((how) => (
                      <td key={how}>
                        {can(event, how) ? (
                          <input
                            type="checkbox"
                            checked={pref[how]}
                            aria-label={`${t(`lists.channel.${how}`)}: ${t(`lists.event.${event}.name`)}`}
                            onChange={() => {
                              flip(event, how);
                            }}
                          />
                        ) : (
                          <span className="quiet" title={why(event, how)}>
                            {event === 'invite' && how === 'email' ? '✓' : '—'}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                )
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="lists-telling">
        <button
          type="button"
          className="button button-small"
          disabled={!notices.mail}
          onClick={() => {
            void testMail().then(
              (sent) => {
                setSaid(t(sent ? 'lists.mailSent' : 'lists.mailFailed'));
              },
              () => {
                setSaid(t('lists.mailFailed'));
              },
            );
          }}
        >
          {t('lists.testMail')}
        </button>
        {notices.telegram.on &&
          (notices.telegram.tied ? (
            <button
              type="button"
              className="button button-small"
              onClick={() => {
                void untieTelegram().then(() => {
                  onNotices({ ...notices, telegram: { on: true, tied: false } });
                }, ignore);
              }}
            >
              {t('lists.untie')}
            </button>
          ) : (
            <button
              type="button"
              className="button button-small"
              onClick={() => {
                void telegramLink().then(
                  (link) => {
                    window.open(link, '_blank', 'noopener');
                    setSaid(t('lists.tieHow'));
                  },
                  () => {
                    setSaid(t('lists.noBot'));
                  },
                );
              }}
            >
              {t('lists.tie')}
            </button>
          ))}
        <span className="small quiet">{t('lists.mailNote')}</span>
      </div>
      {said && (
        <p className="small" role="status">
          {said}
        </p>
      )}
    </section>
  );
}

/**
 * Before the trip (the `Lists` mockup): what to do before leaving — the list's own with
 * reminders, and the tasks the trip sets itself — the trip's files, a stop's things done changed
 * at once, and how one is told.
 */
export function Lists() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const { version: changed } = useLive();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [notices, setNoticesState] = useState<Notices | null>(null);
  const [again, setAgain] = useState(0);
  useEffect(() => {
    let live = true;
    Promise.all([getPlan(id), getLists(id), getDocs(id)]).then(
      ([plan, lists, docs]) => {
        if (live) {
          setState({ kind: 'ready', plan, tasks: lists.tasks, documents: docs.documents });
        }
      },
      (error: unknown) => {
        if (live) setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
    return () => {
      live = false;
    };
  }, [id, changed, again]);
  useEffect(() => {
    let live = true;
    getNotices().then((said) => {
      if (live) setNoticesState(said);
      // The zone at home, as this device says, kept for reminders said as a day.
      const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (said.zone !== zone) void setZone(zone).catch(ignore);
    }, ignore);
    return () => {
      live = false;
    };
  }, []);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}
        </p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }
  const reload = () => {
    setAgain((n) => n + 1);
  };
  return (
    <div className="page lists-page">
      <header className="page-head">
        <h1>{t('lists.title')}</h1>
        <p className="lead">{t('lists.lead')}</p>
      </header>
      {/* A trip read only: its lists said, nothing ticked, added or changed. */}
      <ReadOnlySet>
        <div className="lists-two">
          <Tasks plan={state.plan} tasks={state.tasks} onChanged={reload} />
          <Files plan={state.plan} documents={state.documents} onChanged={reload} />
        </div>
        <Things
          key={state.plan.stops.map((stop) => stop.id).join()}
          plan={state.plan}
          onPlan={(plan) => {
            setState({ ...state, plan });
          }}
        />
      </ReadOnlySet>
      {notices && <Telling notices={notices} onNotices={setNoticesState} />}
    </div>
  );
}
